import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Hero from './components/Hero';
import PainPoints from './components/PainPoints';
import WhyChooseUs from './components/WhyChooseUs';
import Industries from './components/Industries';
import ProcessFlow from './components/ProcessFlow';
import ComplianceStrip from './components/ComplianceStrip';
import Footer from '../../components/feature/Footer';

const services = [
  {
    icon: 'ri-settings-3-line',
    title: 'Managed IT Services',
    tagline: 'Proactive support and monitoring to keep systems running without interruption.',
    description: 'We actively monitor, manage, and maintain your entire IT environment — catching problems before they become outages.',
    tag: 'Most Popular',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Backup & Recovery',
    tagline: 'Secure backups with fast, reliable restoration when failures occur.',
    description: 'On-site and cloud backup systems with regular testing. When systems fail, we restore in minutes — not days.',
    tag: 'Core Service',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Disaster Recovery',
    tagline: 'Recovery plans designed to minimize downtime and protect operations.',
    description: 'Custom DR plans built specifically for your risks — tested and documented. You know exactly how fast you\'ll be back.',
    tag: 'Critical',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud Services',
    tagline: 'Secure, scalable cloud infrastructure for performance and flexibility.',
    description: 'From private cloud storage to full Microsoft 365 and Azure — we architect and manage cloud environments to match your needs.',
    tag: 'Scalable',
  },
  {
    icon: 'ri-team-line',
    title: 'Co-Managed IT',
    tagline: 'Additional support for internal IT teams that need more depth.',
    description: 'Work alongside your existing team — filling expertise gaps, adding 24/7 coverage, and handling overflow.',
    tag: 'Flexible',
  },
];

const testimonials = [
  {
    quote: "We had Mirrored Storage as our backup & disaster recovery company. All was well until a crypto virus attack that encrypted all our servers and we were out of business. We called Mirrored Storage and told them we need recovery ASAP — and they DELIVERED!!!",
    author: 'Existing Customer',
    company: 'Travel Services Company',
    highlight: 'Recovered from crypto virus attack',
  },
  {
    quote: "Our hardware failed right at the worst time — end of the year. Mirrored Storage was able to get our backed-up server running on their local appliance in just a few minutes. We were back online before we even fully understood what had happened.",
    author: 'Existing Customer',
    company: 'Manufacturing & Distribution Company',
    highlight: 'Back online in minutes after hardware failure',
  },
  {
    quote: "I thought we were covered with our tape backup, but they discovered in just a few minutes that we were NOT covered. In fact, we were at great risk of massive data loss! They know what they are doing.",
    author: 'Chief Financial Officer',
    company: 'Manufacturing',
    highlight: 'Discovered critical backup failure before disaster struck',
  },
];

const HomePage = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      {/* White navbar */}
      <Navbar />

      {/* 1. Hero — DARK */}
      <Hero />

      {/* Trust strip — DARK continuation */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-center lg:justify-between gap-x-6 gap-y-3">
            {[
              { icon: 'ri-phone-fill', text: '24/7 Support' },
              { icon: 'ri-timer-flash-line', text: 'Proven Systems' },
              { icon: 'ri-flashlight-line', text: 'Fast Response' },
              { icon: 'ri-shield-check-line', text: 'Reliable Execution' },
              { icon: 'ri-map-pin-2-line', text: 'DFW-Based Team' },
              { icon: 'ri-lock-line', text: 'Never Outsourced' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-teal-400 text-sm`} />
                </div>
                <span className="text-slate-400 text-sm font-medium whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. Pain Points — LIGHT (white) */}
      <PainPoints />

      {/* 3. Services — DARK */}
      <section className="py-24 lg:py-32 bg-slate-950 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <span className="text-teal-400 font-bold text-xs uppercase tracking-widest block mb-4">
                What We Do
              </span>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-[1.05] tracking-tight max-w-xl">
                Complete Business Continuity —{' '}
                <span className="text-slate-500">Not Just IT Support</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-teal-400 font-bold hover:text-teal-300 whitespace-nowrap transition-colors flex-shrink-0 text-sm"
            >
              Full Service Details
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, i) => (
              <div
                key={i}
                className="group relative bg-slate-900 hover:bg-slate-900/80 rounded-2xl p-7 transition-all duration-200 cursor-default border border-slate-800 hover:border-teal-500/30"
              >
                <span className="absolute top-5 right-5 text-[10px] font-bold text-teal-400 bg-teal-500/8 border border-teal-500/15 px-2.5 py-1 rounded-full tracking-wide">
                  {service.tag}
                </span>
                <div className="w-11 h-11 bg-slate-800 group-hover:bg-teal-500/10 border border-slate-700 group-hover:border-teal-500/20 rounded-xl flex items-center justify-center mb-6 transition-all duration-200">
                  <i className={`${service.icon} text-lg text-teal-400`} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{service.title}</h3>
                <p className="text-teal-400 font-semibold text-xs mb-3 leading-snug">{service.tagline}</p>
                <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}

            {/* CTA card */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 border border-teal-500/30 rounded-2xl p-7 flex flex-col justify-between">
              <div>
                <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-stethoscope-line text-lg text-white" />
                </div>
                <h3 className="text-base font-bold text-white mb-2">Not sure where to start?</h3>
                <p className="text-teal-100/80 text-sm leading-relaxed">
                  We'll audit your current setup for free and identify exactly where your risks are.
                </p>
              </div>
              <Link
                to="/locations"
                className="inline-flex items-center gap-2 bg-white text-teal-700 font-bold px-5 py-2.5 rounded-lg mt-6 hover:bg-teal-50 transition-colors whitespace-nowrap w-fit cursor-pointer text-sm"
              >
                Free Assessment
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Why Choose Us — LIGHT (slate-50) */}
      <WhyChooseUs />

      {/* 5. Industries — DARK */}
      <Industries />

      {/* 6. Testimonials — LIGHT (white) */}
      <section className="py-24 lg:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14">
            <span className="text-teal-600 font-bold text-xs uppercase tracking-widest block mb-4">
              Client Stories
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-slate-950 leading-[1.05] tracking-tight">
              When It Counted Most,{' '}
              <span className="text-slate-400">We Delivered</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className={`text-left rounded-2xl p-7 transition-all duration-200 cursor-pointer border ${
                  activeTestimonial === i
                    ? 'bg-slate-950 border-slate-700'
                    : 'bg-slate-50 border-slate-100 hover:border-slate-200'
                }`}
              >
                <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-5 ${
                  activeTestimonial === i
                    ? 'bg-teal-500/15 text-teal-400 border border-teal-500/25'
                    : 'bg-teal-50 text-teal-600 border border-teal-100'
                }`}>
                  <i className="ri-checkbox-circle-fill" />
                  {t.highlight}
                </div>

                <blockquote className={`text-sm leading-relaxed mb-6 ${
                  activeTestimonial === i ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <div className={`flex items-center gap-3 pt-5 border-t ${
                  activeTestimonial === i ? 'border-slate-800' : 'border-slate-100'
                }`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activeTestimonial === i ? 'bg-teal-500/12 border border-teal-500/20' : 'bg-slate-100'
                  }`}>
                    <i className={`ri-user-line text-xs ${activeTestimonial === i ? 'text-teal-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className={`font-bold text-xs ${activeTestimonial === i ? 'text-white' : 'text-slate-900'}`}>{t.author}</p>
                    <p className={`text-[11px] ${activeTestimonial === i ? 'text-teal-400' : 'text-teal-600'}`}>{t.company}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Process Flow WOW — DARK */}
      <ProcessFlow />

      {/* 8. Compliance Strip — LIGHT (white) */}
      <ComplianceStrip />

      {/* 9. Final CTA — DARK */}
      <section className="py-28 lg:py-36 bg-slate-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(20,184,166,0.07)_0%,_transparent_65%)] pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 border border-teal-500/25 bg-teal-500/5 rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
            <span className="text-teal-400 text-xs font-bold tracking-widest uppercase">Let's Build Something Reliable</span>
          </div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-[1.04] tracking-tight mb-6">
            Need a More Reliable<br />IT Partner?
          </h2>

          <p className="text-slate-400 text-lg leading-relaxed mb-2 max-w-xl mx-auto">
            Stop dealing with downtime, slow support, and unreliable systems.
          </p>
          <p className="text-slate-600 text-base leading-relaxed mb-12 max-w-lg mx-auto">
            Let's build a secure, stable IT environment for your business.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/locations"
              className="inline-flex items-center justify-center gap-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold px-9 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Schedule a Consultation
              <i className="ri-calendar-check-line" />
            </Link>
            <a
              href="tel:2145500550"
              className="inline-flex items-center justify-center gap-2.5 border border-slate-700 hover:border-teal-500/40 bg-slate-900/50 hover:bg-slate-900 text-white text-sm font-bold px-9 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-teal-400" />
              Call Now — (214) 550-0550
            </a>
          </div>
        </div>
      </section>

      {/* White footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
